import type { SeverityLevel } from "@sentry/react";
import type { GraphQLFormattedError } from "graphql";

/**
 * Codes the API sets in `extensions.code` of a GraphQL error, following the codes used by Apollo Server.
 * Mirrors `OnSinch\App\GraphQL\Error\GraphQLErrorCode` on the backend.
 */
export enum GraphqlErrorCode {
  /** Persisted query id of this bundle is unknown to the server, so the bundle is older than the deployment */
  PersistedQueryNotFound = "PERSISTED_QUERY_NOT_FOUND",
  /** Server does not accept persisted queries at all - handled defensively, this API always requires them */
  PersistedQueryNotSupported = "PERSISTED_QUERY_NOT_SUPPORTED",
  /** Stored query body could not be parsed, meaning a corrupted manifest entry */
  GraphqlParseFailed = "GRAPHQL_PARSE_FAILED",
  /** Query does not match the schema - not emitted by this API, persisted queries are validated at build time */
  GraphqlValidationFailed = "GRAPHQL_VALIDATION_FAILED",
  /** Operation to execute could not be determined from the request */
  OperationResolutionFailure = "OPERATION_RESOLUTION_FAILURE",
  /** Variables do not match the schema, or a domain rule rejected the input */
  BadUserInput = "BAD_USER_INPUT",
  /** Request itself is malformed - unreadable body, unsupported method, missing persisted query id */
  BadRequest = "BAD_REQUEST",
  /** Anything unexpected on the server side */
  InternalServerError = "INTERNAL_SERVER_ERROR",
}

/**
 * Codes meaning this bundle can no longer talk to the server, whatever it sends. Retrying is pointless,
 * the page has to be reloaded to get the current bundle. `PersistedQueryNotSupported` belongs here as well:
 * the fallback Apollo would use for it (sending the query text) is refused by this API.
 */
const OUTDATED_CLIENT_CODES: string[] = [
  GraphqlErrorCode.PersistedQueryNotFound,
  GraphqlErrorCode.PersistedQueryNotSupported,
];

/**
 * Codes describing input the server rejected on purpose. The view shows the message to the user, so they are
 * worth keeping in Sentry as a warning only, not as an application error.
 */
const EXPECTED_CODES: string[] = [GraphqlErrorCode.BadUserInput];

/**
 * Codes carried by response errors, in the order the server sent them
 */
export const graphqlErrorCodes = (graphQLErrors: readonly GraphQLFormattedError[] | undefined): string[] =>
  (graphQLErrors ?? [])
    .map(({ extensions }) => extensions?.code)
    .filter((code): code is string => typeof code === "string");

/**
 * Whether the response says that this bundle is out of sync with the server
 */
export const isOutdatedClientError = (graphQLErrors: readonly GraphQLFormattedError[] | undefined): boolean =>
  graphqlErrorCodes(graphQLErrors).some((code) => OUTDATED_CLIENT_CODES.includes(code));

/**
 * Severity a failure carrying these codes is reported with. Errors the server rejected on purpose are a warning,
 * anything else (including a response without any code) stays an error.
 */
export const graphqlErrorLevel = (codes: string[]): SeverityLevel =>
  codes.length > 0 && codes.every((code) => EXPECTED_CODES.includes(code)) ? "warning" : "error";
